import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { authAPI } from '../../utils/adminAPI';

// Sessão do admin/gerenciador logado ({ id, tipo, email, nome_completo }), disponível pra
// qualquer página dentro de RequireAuth via useAdminAuth() — evita que cada
// página tenha que chamar authAPI.me() de novo só pra saber quem está
// logado (antes disso era repetido em quase toda página do painel).
const AdminAuthContext = createContext(null);

// Contexto separado (não muda o formato do que useAdminAuth() já devolve,
// pra não quebrar nenhum consumidor existente) só com a função de recarregar
// a sessão — usada depois que o próprio usuário edita nome/foto no modal de
// perfil (AdminLayout), pra refletir na hora sem precisar de F5.
const AdminAuthRefreshContext = createContext(() => {});

export function useAdminAuth() {
  return useContext(AdminAuthContext);
}

export function useAdminAuthRefresh() {
  return useContext(AdminAuthRefreshContext);
}

// Envolve rotas que exigem sessão válida (admin ou gerenciador — mesmas
// atribuições sobre enquetes). Passe somenteAdmin pra restringir também por
// tipo (ferramentas de segurança: IPs bloqueados, log de auditoria, gestão
// de contas admin).
// Confirma a sessão com o backend (via cookie httpOnly) a cada carregamento,
// já que o frontend não tem acesso ao conteúdo do cookie para checar sozinho.
const RequireAuth = ({ children, somenteAdmin = false }) => {
  const [status, setStatus] = useState('checking'); // 'checking' | 'authenticated' | 'unauthenticated' | 'forbidden'
  const [sessao, setSessao] = useState(null);

  const recarregarSessao = useCallback(() => {
    return authAPI.me().then((dados) => {
      if (somenteAdmin && dados.tipo !== 'admin') {
        setStatus('forbidden');
      } else {
        setSessao(dados);
        setStatus('authenticated');
      }
    });
  }, [somenteAdmin]);

  useEffect(() => {
    let ativo = true;

    authAPI.me()
      .then((dados) => {
        if (!ativo) return;
        if (somenteAdmin && dados.tipo !== 'admin') {
          setStatus('forbidden');
        } else {
          setSessao(dados);
          setStatus('authenticated');
        }
      })
      .catch(() => {
        if (ativo) setStatus('unauthenticated');
      });

    return () => {
      ativo = false;
    };
  }, [somenteAdmin]);

  if (status === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/admin/login" replace />;
  }

  if (status === 'forbidden') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="card max-w-md text-center">
          <h1 className="text-lg font-bold text-gray-900 mb-2">Acesso restrito</h1>
          <p className="text-gray-600">Essa área é exclusiva para administradores.</p>
        </div>
      </div>
    );
  }

  return (
    <AdminAuthContext.Provider value={sessao}>
      <AdminAuthRefreshContext.Provider value={recarregarSessao}>
        {children}
      </AdminAuthRefreshContext.Provider>
    </AdminAuthContext.Provider>
  );
};

export default RequireAuth;
